<template>
    <div
        :class="[
            'z-date-picker',
            {
                'is-disabled': this.disabled,
                'is-filled': isFilled,
                'is-errored': isValid === false,
                'is-valid': isValid
            }
        ]"
    >
        <date-picker
            v-bind="$attrs"
            :key="lang"
            :type="type"
            :lang="lang"
            v-model="date"
            :range="range"
            :format="format"
            @change="change"
            :title-format="format"
            :clearable="clearable"
            :value-type="valueType"
            :placeholder="placeholder"
            :disabled-date="disabledDate"
            popup-class="z-date-picker-popup"
            :append-to-body="appendToBody"
        >
            <template v-slot:icon-calendar>
                <z-icon name="calendar" dir="top" width="24" height="24"> </z-icon>
            </template>

            <template v-slot:icon-clear>
                <z-icon name="close" dir="top" width="19" height="19"> </z-icon>
            </template>
        </date-picker>
        <span
            :class="['z-date-picker__error', errorClass]"
            v-html="error"
            v-if="error && !isValid"
        ></span>
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { localize } from '@/utils/i18n'

DatePicker.methods.displayPopup = function () {
    this.position = {
        left: 0,
        bottom: '100%'
    }
}

export default {
    name: 'z-date-picker',
    components: {
        DatePicker
    },
    props: {
        range: Boolean,
        disabled: Boolean,
        clearable: {
            type: Boolean,
            default: true
        },
        required: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'date',
            validator: (prop) =>
                ['date', 'datetime', 'year', 'month', 'time', 'week'].includes(prop)
        },
        valueType: {
            type: String,
            default: 'X'
        },
        placeholder: {
            type: String,
            default: ''
        },
        format: {
            type: String,
            default: 'DD.MM.YYYY'
        },
        value: [Array, String],
        disabledDate: {
            type: Function,
            default: () => {}
        },
        errorClass: {
            type: String,
            default: ''
        },
        appendToBody: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            isValid: null,
            date: this.value,
            lang: this.$root.lang,
            error: '',
            text: {
                error: {
                    ru: 'Поле обязательно для заполнения',
                    en: 'Required field'
                }
            }
        }
    },
    created () {
        import(`vue2-datepicker/locale/${this.lang}`)
    },
    watch: {
        value (value) {
            this.date = value
        }
    },
    computed: {
        isFilled () {
            if (Array.isArray(this.date)) {
                return Boolean(this.date[0] && this.date[1])
            }

            return Boolean(this.date)
        }
    },
    methods: {
        change (data) {
            let result = data
            if (Array.isArray(data) && this.valueType === 'X') {
                const newValue = String(+data[1] + ((23 * 60 + 59) * 60 + 59))
                data.splice(1, 1, newValue)
            }
            if (Array.isArray(data) && !(data[0] && data[1])) {
                result = undefined
            }

            this.$emit('input', result)
            this.$emit('change', result)
            this.validate()
        },
        validate () {
            if (this.required && !this.isFilled) {
                this.isValid = false
                this.error = localize(this.text.error)
                return
            }

            this.isValid = true
            this.error = ''
        }
    }
}
</script>

<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';
$errorColorLight: $token-colors-alert-text;
$errorColor: $token-colors-alert-text;
$validColor: $token-colors-success-text;
$hoverColor: $token-colors-text-hover;
$focusColor: $token-colors-text-focus;
$defaultColor: $token-colors-text-default;

.z-date-picker {
    font-size: 16px;

    &__error {
        font-size: 0.8125em;
        color: $errorColor;
        position: relative;
        left: 20px;
    }

    .mx-datepicker {
        width: 100%;
        position: static;
    }

    .mx-input-wrapper {
        width: 100%;
        position: relative;
        display: inline-block;
        width: 100%;
    }

    .mx-input {
        box-shadow: inset 0 0 0 1px $token-colors-black-40;
        padding: 12px 20px;
        box-sizing: border-box;
        font-size: 1em;
        height: auto;
        transition: border 0.2s ease-in;
        line-height: 1.5;
        border-radius: 4px;
        border: none !important;
        color: $token-colors-text-default;

        &::placeholder {
            color: $token-colors-text-default;
        }

        @include breakpoint(v-tablet) {
            padding-right: 32px;
        }

        &:hover {
            border-color: $hoverColor;
        }

        &:focus {
            border-color: $focusColor;
        }
    }

    .mx-icon-calendar {
        position: absolute;
        display: block !important;
        transition: background 0.2s ease-in;
        width: 24px;
        height: 24px;
        right: 10px;
        top: 50%;
        transform: translate(0, -50%);

        @include breakpoint(v-tablet) {
            width: 24px;
            height: 24px;
        }
    }

    .mx-icon-clear {
        position: absolute;
        display: block;
        right: 44px;
        font-size: 1.125em;
        transition: color 0.2s ease-in;
        top: 50%;
        transform: translate(0, -50%);
        &:hover {
            color: $hoverColor;
        }
    }

    // filled state
    &.is-filled {
        .mx-input {
            border-color: $focusColor;
        }
    }

    // valid state
    &.is-valid {
        .mx-input {
            // border-color: $validColor;
        }
    }

    // errored state
    &.is-errored {
        .mx-input {
            border-color: $errorColorLight;
        }
    }
}

.z-date-picker-popup {
    .mx-btn-text {
        color: inherit;
        font-size: inherit;
        text-transform: capitalize;
        font-size: 22px;
        font-weight: 400;

        @include breakpoint(v-tablet) {
            font-size: 18px;
        }
    }

    .mx-calendar {
        padding: 24px 24px 16px;
        width: 336px;
        @include breakpoint(v-tablet) {
            padding: 16px;
        }
    }

    .mx-calendar-header {
        padding: 0;
        margin-bottom: 24px;
        height: auto;
        line-height: 1;

        @include breakpoint(v-tablet) {
            margin-bottom: 16px;
        }
    }

    .mx-calendar-header-label {
        font-weight: 700;
        color: #000;
        font-size: 14px;
    }

    .mx-calendar-content {
        height: auto;
    }

    .mx-icon-left:before,
    .mx-icon-right:before,
    .mx-icon-double-left:before,
    .mx-icon-double-right:before,
    .mx-icon-double-left:after,
    .mx-icon-double-right:after {
        width: 16px;
        height: 16px;
        color: $token-colors-black-40;
        border-width: 3px 0 0 3px;

        @include breakpoint(v-tablet) {
            width: 14px;
            height: 14px;
        }
    }

    .mx-table-month,
    .mx-table-year {
        .cell {
            div {
                width: auto;
            }

            &.active {
                div {
                    width: 70px;
                    margin: 0 auto;
                }
            }
        }
    }

    .cell {
        color: $token-colors-black-60;
        font-weight: 400;
        font-size: 13px;
        line-height: 1;
        padding: 4px;

        div {
            margin: 0;
            min-width: 32px;
            min-height: 32px;
            line-height: 32px;
            transition: background 0.2s ease-in;
            max-width: 100%;
            max-height: 100%;
            border-radius: 4px;

            @include breakpoint(v-tablet) {
                width: 24px;
                height: 24px;
            }
        }

        &.active {
            background: $token-colors-white;

            div {
                background: $token-colors-secondary !important;
            }
        }

        &.in-range {
            background: $token-colors-white;

            div {
                background: $token-colors-secondary;
                color: $token-colors-white;
            }
        }

        &.hover-in-range {
            background-color: $token-colors-secondary-bg;
        }

        &.today {
            background: $token-colors-white;
            color: $token-colors-white;

            div {
                background: $token-colors-secondary !important;
            }
        }

        &:hover {
            background-color: $token-colors-white;
            div {
                box-shadow: inset 0 0 0 2px $token-colors-border-hover;
                width: 70px;
                margin: 0 auto;
            }
        }
    }

    .mx-btn-current-year {
        font-size: 22px;
        color: $token-colors-link-hover;
    }

    .mx-btn-current-month {
        font-size: 22px;
        color: $token-colors-link-default;
    }

    .mx-table {
        margin: 0;

        th {
            color: $token-colors-black-60;
            font-weight: 400;
            font-size: 13px;
        }
    }

    /* nav arrows */
    .mx-icon-right,
    .mx-btn-icon-double-right,
    .mx-btn-icon-left,
    .mx-btn-icon-double-left {
        color: #000;
        transition: color 0.2s ease-in;

        &:hover {
            color: $hoverColor;
        }
    }
    /* /nav arrows */
}
</style>
